import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Modal, Spin, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { useAppMessage } from "../../../hooks/useAppMessage";
import AdminHeaderTab from "../../../Components/headerTab/AdminHeaderTab";
import { RankingAlgorithmSettingsAPI } from "../../../API/admin/RankingAlgorithmSettinsAPI";
import { useFlightModel } from "../../../contexts/FlightModelContext";
import {
  AlgorithmSettingsCard,
  AlgorithmOverviewGuide,
  AlgorithmComparisonTable,
} from "./components";
import "../../../styles/Playground/Playground.css";
import "../../../styles/admin/RankingAlgorithmSetting.css";

const { Text } = Typography;
const { confirm } = Modal;

function RankingAlgorithmSettins() {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = useAppMessage();
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();

  // Parameter State
  const [parameterConfig, setParameterConfig] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  // Simulation & Comparison Table State
  const [tableLoading, setTableLoading] = useState(false);
  const [simulationRows, setSimulationRows] = useState([]);
  const [hasSimulated, setHasSimulated] = useState(false);
  const [lastSimulatedWeights, setLastSimulatedWeights] = useState(null);
  const lastSimulatedWeightsRef = useRef(null);

  // Search & Pagination State
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const comparisonRef = useRef(null);

  const messageToast = useCallback(
    (type, content) => {
      messageApi.open({ type, content, duration: 6 });
    },
    [messageApi]
  );

  // Clear search whenever comparison grid is closed / reset
  useEffect(() => {
    if (!hasSimulated) {
      clearTimeout(searchTimerRef.current);
      setSearchInput("");
      setSearchText("");
    }
  }, [hasSimulated]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const anyModified = useMemo(
    () => parameterConfig.some((param) => param.valueFrom !== ""),
    [parameterConfig]
  );

  const isStale = useMemo(() => {
    if (!hasSimulated || !lastSimulatedWeights) return false;
    const anyImportanceSelected = parameterConfig.some(
      (param) => param.valueFrom !== ""
    );
    if (!anyImportanceSelected) return false;
    return parameterConfig.some(
      (param) => param.valueFrom !== lastSimulatedWeights[param.key]
    );
  }, [hasSimulated, lastSimulatedWeights, parameterConfig]);

  const resolveFlightModel = useCallback(async () => {
    let flightModelToUse = selectedFlightModel;
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }
    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");
    }
    return flightModelToUse;
  }, [selectedFlightModel, refreshFlightModels, messageToast]);

  const getRankingWeights = useCallback(async () => {
    try {
      setPageLoading(true);
      let flightModelToUse = selectedFlightModel;
      if (!flightModelToUse) {
        const refreshed = await refreshFlightModels();
        flightModelToUse = refreshed?.selectedFlightModel || null;
      }

      if (!flightModelToUse) {
        messageToast("error", "No flight model selected.");
        setPageLoading(false);
        return;
      }

      const queryParams = {
        params: {
          flight_model: flightModelToUse,
        },
      };

      const rankingRes = await RankingAlgorithmSettingsAPI.getRankingWeights(queryParams);
      const parameters = rankingRes?.data?.parameters;

      if (rankingRes?.data?.status && Array.isArray(parameters)) {
        const config = parameters.map((item) => ({
          key: item.parameter,
          label: item.label,
          valueFrom: "",
          valueTo: item.weight,
        }));

        setParameterConfig(config);
      } else {
        messageToast("error", rankingRes?.data?.message || "Failed to load ranking weights");
      }
    } catch {
      messageToast("error", "Internal Server Error");
    } finally {
      setPageLoading(false);
    }
  }, [messageToast, refreshFlightModels, selectedFlightModel]);

  useEffect(() => {
    getRankingWeights();
  }, [getRankingWeights]);

  const handleToggleEdit = useCallback(() => {
    if (isEditing) {
      // Revert What-If values back to live baseline
      setParameterConfig((prev) =>
        prev.map((param) => ({
          ...param,
          valueFrom: "",
          updatedWeight: null,
        }))
      );
      setHasSimulated(false);
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [isEditing]);

  const handleImportanceChange = useCallback((key, value) => {
    setParameterConfig((prev) =>
      prev.map((param) =>
        param.key === key ? { ...param, valueFrom: value } : param
      )
    );
  }, []);

  const handleResetToCurrent = useCallback(() => {
    setParameterConfig((prev) =>
      prev.map((param) => ({
        ...param,
        valueFrom: "",
        updatedWeight: null,
      }))
    );
    setSimulationRows([]);
    setLastSimulatedWeights(null);
    lastSimulatedWeightsRef.current = null;
    setHasSimulated(false);
    setPage(1);
    setTotalRecords(0);
  }, []);

  const handleResetToDefault = useCallback(() => {
    confirm({
      title: "Reset to default weights?",
      icon: <ExclamationCircleFilled />,
      content:
        "This will restore all ranking algorithm parameters to system default weights.",
      okText: "Reset to Defaults",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        setPageLoading(true);
        try {
          const flightModelToUse = await resolveFlightModel();
          if (!flightModelToUse) return;

          const res = await RankingAlgorithmSettingsAPI.restoreDefaultWeights({
            flight_model: flightModelToUse,
          });
          const resData = res?.data?.status !== undefined ? res.data : (res?.data || res);

          if (resData?.status === true || res?.status === true) {
            const updatedParams = Array.isArray(resData?.parameters)
              ? resData.parameters
              : [];
            setParameterConfig((prev) =>
              prev.map((param) => {
                const match = updatedParams.find((p) => p.parameter === param.key);
                return {
                  ...param,
                  valueFrom: "",
                  valueTo: match ? match.weight : param.valueTo,
                  updatedWeight: null,
                };
              })
            );
            setSimulationRows([]);
            setLastSimulatedWeights(null);
            lastSimulatedWeightsRef.current = null;
            setHasSimulated(false);
            messageToast(
              "success",
              resData?.message || "Configuration reset to default successfully."
            );
          } else {
            messageToast("error", resData?.message || "Failed to reset configuration to default.");
          }
        } catch {
          messageToast("error", "Failed to reset configuration to default.");
        } finally {
          setPageLoading(false);
        }
      },
    });
  }, [resolveFlightModel, messageToast]);

  const [totalRecords, setTotalRecords] = useState(0);

  const runSimulation = useCallback(
    async (
      pageNum = 1,
      pageSizeNum = pageSize,
      search = searchText,
      weightsOverride = null
    ) => {
      const flightModelToUse = await resolveFlightModel();
      if (!flightModelToUse) return;

      const rawSelections =
        weightsOverride ||
        parameterConfig.reduce((acc, { key, valueFrom }) => {
          acc[key] = valueFrom;
          return acc;
        }, {});

      const importance = Object.fromEntries(
        Object.entries(rawSelections).map(([key, value]) => [
          key,
          value !== "" ? value : 1,
        ])
      );

      const bodyData = {
        flight_model: flightModelToUse,
        importance,
        page: pageNum,
        page_size: pageSizeNum,
        ...(search ? { search } : {}),
      };

      setTableLoading(true);
      try {
        const res = await RankingAlgorithmSettingsAPI.simulateRanking(bodyData);

        const resData = res?.data?.status !== undefined ? res.data : (res?.data || res);
        const isSuccess = resData?.status === true || res?.status === true;

        if (isSuccess) {
          const rawRows = Array.isArray(resData?.rows) ? resData.rows : [];

          const mappedRows = rawRows.map((row, index) => ({
            key: `${row.spec_concat || "spec"}_${row.card_no || index}`,
            spec_concat: row.spec_concat,
            card_no: row.card_no,
            rankPrevious: row.previous?.rank,
            rankWhatIf: row.what_if?.rank,
            rankDelta: row.change?.rank_delta,
            direction: row.change?.direction,
            wpmPrevious: row.previous?.wpm_score,
            wpmWhatIf: row.what_if?.wpm_score,
            wpmDelta: row.change?.wpm_score_delta,
            costPrevious: row.previous?.total_labor_cost_max,
            costWhatIf: row.what_if?.total_labor_cost_max,
            costDelta: row.change?.cost_delta,
          }));

          setSimulationRows(mappedRows);
          setPage(resData?.page || pageNum);
          setPageSize(resData?.page_size || pageSizeNum);

          const calculatedTotal =
            resData?.summary?.total_specs ??
            (resData?.total_pages && resData?.page_size
              ? resData.total_pages * resData.page_size
              : rawRows.length);
          setTotalRecords(calculatedTotal);

          const simWeights = Array.isArray(resData?.weights)
            ? resData.weights
            : Array.isArray(res?.data?.weights)
            ? res?.data?.weights
            : Array.isArray(res?.weights)
            ? res.weights
            : [];

          if (simWeights.length > 0) {
            setParameterConfig((prev) =>
              prev.map((param) => {
                const match = simWeights.find(
                  (w) => w.parameter === param.key
                );
                return {
                  ...param,
                  updatedWeight: match ? match.what_if_weight : param.updatedWeight,
                };
              })
            );
          }

          setLastSimulatedWeights(rawSelections);
          lastSimulatedWeightsRef.current = rawSelections;

          setHasSimulated(true);

          setTimeout(() => {
            comparisonRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }, 150);
        } else {
          messageToast("error", resData?.message || "Failed to simulate ranking algorithm.");
        }
      } catch {
        messageToast("error", "Internal Server Error");
      } finally {
        setTableLoading(false);
      }
    },
    [resolveFlightModel, parameterConfig, pageSize, searchText, messageToast]
  );

  const handleRunSimulation = useCallback(() => {
    clearTimeout(searchTimerRef.current);
    setSearchInput("");
    setSearchText("");
    runSimulation(1, pageSize, "");
  }, [runSimulation, pageSize]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        const trimmed = value.trim();
        setSearchText(trimmed);
        runSimulation(1, pageSize, trimmed, lastSimulatedWeightsRef.current);
      }, 350);
    },
    [runSimulation, pageSize]
  );

  const handleTableChange = useCallback(
    (pagination) => {
      const { current, pageSize: newSize } = pagination;
      runSimulation(current, newSize, searchText, lastSimulatedWeightsRef.current);
    },
    [runSimulation, searchText]
  );

  const handleDiscard = useCallback(() => {
    if (!anyModified) {
      navigate("/ranking");
      return;
    }
    confirm({
      title: "Discard changes and exit?",
      icon: <ExclamationCircleFilled />,
      content:
        "Your hypothetical values have not been applied. Exiting now will discard them and the live configuration will remain unchanged.",
      okText: "Discard & Exit",
      okType: "danger",
      cancelText: "Stay",
      onOk() {
        handleResetToCurrent();
        setIsEditing(false);
        setHasSimulated(false);
        navigate("/ranking");
      },
    });
  }, [anyModified, handleResetToCurrent, navigate]);

  const applyChanges = useCallback(async () => {
    const flightModelToUse = await resolveFlightModel();
    if (!flightModelToUse) return;

    const importance = parameterConfig.reduce((acc, { key, valueFrom }) => {
      acc[key] = valueFrom !== "" ? valueFrom : 1;
      return acc;
    }, {});

    const bodyData = {
      flight_model: flightModelToUse,
      importance,
      page: 1,
      page_size: pageSize,
      search: searchText || null,
    };

    setPageLoading(true);
    try {
      const res = await RankingAlgorithmSettingsAPI.applyRankingWeights(bodyData);
      const resData = res?.data?.status !== undefined ? res.data : (res?.data || res);
      const isSuccess = resData?.status === true || res?.status === true;

      if (isSuccess) {
        const updatedParams = Array.isArray(resData?.parameters)
          ? resData.parameters
          : [];

        setParameterConfig((prev) =>
          prev.map((param) => {
            const match = updatedParams.find((p) => p.parameter === param.key);
            return {
              ...param,
              valueFrom: "",
              valueTo: match ? match.weight : param.valueTo,
              updatedWeight: null,
            };
          })
        );

        setHasSimulated(false);
        setSimulationRows([]);
        setLastSimulatedWeights(null);
        lastSimulatedWeightsRef.current = null;
        setIsEditing(false);

        Modal.success({
          title: "Changes Applied",
          content: (
            <>
              <Text>
                {resData?.message ||
                  "Changes applied. The Ranking Grid has been recalculated and updated for all users."}
              </Text>
              {resData?.warning && (
                <Text type="warning" style={{ display: "block", marginTop: 8 }}>
                  {resData.warning}
                </Text>
              )}
              {resData?.impact && (
                <Text
                  type="secondary"
                  style={{ display: "block", marginTop: 8, fontSize: "12px" }}
                >
                  Live rankings changed for {resData.impact.specs_rank_changed} of{" "}
                  {resData.impact.total_specs} specification(s).
                </Text>
              )}
            </>
          ),
          onOk: () => navigate("/ranking"),
        });
      } else {
        messageToast(
          "error",
          resData?.message || res?.message || "Failed to save configuration."
        );
      }
    } catch {
      messageToast("error", "Internal Server Error");
    } finally {
      setPageLoading(false);
    }
  }, [resolveFlightModel, parameterConfig, pageSize, searchText, messageToast, navigate]);

  const handleApplyChanges = useCallback(() => {
    confirm({
      title: "Save these changes to live configuration?",
      icon: <ExclamationCircleFilled />,
      content:
        "The importance values will replace the live configuration and the ranking will be recalculated using the updated values.",
      okText: "Apply Changes",
      cancelText: "Cancel",
      onOk: applyChanges,
    });
  }, [applyChanges]);

  return (
    <div className="container_div">
      <Helmet>
        <title>SpecOptimizer | Ranking Algorithm Settings</title>
      </Helmet>

      <AdminHeaderTab currrentTab="Ranking Algorithm Settings" />

      <Spin spinning={pageLoading || tableLoading}>
        <div className="ranking_algorithm_content_wrap">
          {/* Top Split: Scenario Settings + Live Overview & Simulation Guide */}
          <div className="playground_top_split">
            <AlgorithmSettingsCard
              parameterConfig={parameterConfig}
              isEditing={isEditing}
              hasSimulated={hasSimulated}
              onToggleEdit={handleToggleEdit}
              onImportanceChange={handleImportanceChange}
              onResetToCurrent={handleResetToCurrent}
              onResetToDefault={handleResetToDefault}
              onRunSimulation={handleRunSimulation}
              simulationLoading={tableLoading}
            />

            <AlgorithmOverviewGuide
              selectedFlightModel={selectedFlightModel}
            />
          </div>

          {/* Ranking Comparison (Simulation Result) - Visible after running simulation */}
          {hasSimulated && (
            <AlgorithmComparisonTable
              ref={comparisonRef}
              rankingRows={simulationRows}
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              searchInput={searchInput}
              isSearching={Boolean(searchText)}
              isStale={isStale}
              onSearchChange={handleSearchChange}
              onTableChange={handleTableChange}
              onDiscard={handleDiscard}
              onApplyChanges={handleApplyChanges}
            />
          )}
        </div>
      </Spin>

      {contextHolder}
    </div>
  );
}

export default RankingAlgorithmSettins;

/* =========================================================================================
 * PREVIOUS RANKING ALGORITHM SETTINGS IMPLEMENTATION (COMMENTED OUT FOR REFERENCE)
 * =========================================================================================
 *
 * function OldRankingAlgorithmSettins() {
 *   const [messageApi, contextHolder] = useAppMessage();
 *   const [spinning, setSpinning] = useState(false);
 *   const [parameterConfig, setParameterConfig] = useState([]);
 *   const [simulateRankingModalOpen, setSimulateRankingModalOpen] = useState(false);
 *   const { selectedFlightModel, refreshFlightModels } = useFlightModel();
 *
 *   const messageToast = useCallback(
 *     (type, content) => {
 *       messageApi.open({ type, content, duration: 6 });
 *     },
 *     [messageApi]
 *   );
 *
 *   const getRankingWeights = useCallback(async () => {
 *     try {
 *       setSpinning(true);
 *       let flightModelToUse = selectedFlightModel;
 *       if (!flightModelToUse) {
 *         const refreshed = await refreshFlightModels();
 *         flightModelToUse = refreshed?.selectedFlightModel || null;
 *       }
 *
 *       if (!flightModelToUse) {
 *         messageToast("error", "No flight model selected.");
 *         setSpinning(false);
 *         return;
 *       }
 *
 *       const queryParams = {
 *         params: {
 *           flight_model: flightModelToUse,
 *         },
 *       };
 *
 *       const rankingRes = await RankingAlgorithmSettingsAPI.getRankingWeights(queryParams);
 *
 *       if (rankingRes?.data) {
 *         const config = rankingRes.data.map((item) => ({
 *           key: item.name,
 *           label: item.description,
 *           valueFrom: "",
 *           valueTo: item.value,
 *         }));
 *
 *         setParameterConfig(config);
 *       } else {
 *         messageToast("error", rankingRes?.data?.message);
 *       }
 *     } catch (error) {
 *       messageToast("error", "Internal Server Error");
 *     } finally {
 *       setSpinning(false);
 *     }
 *   }, [messageToast, refreshFlightModels, selectedFlightModel]);
 *
 *   useEffect(() => {
 *     getRankingWeights();
 *   }, [getRankingWeights]);
 *
 *   async function saveConfiguration() {
 *     setSpinning(true);
 *
 *     const bodyData = {
 *       weights: parameterConfig.reduce((acc, { key, valueFrom, valueTo }) => {
 *         acc[key] = valueFrom !== "" ? valueFrom : valueTo;
 *         return acc;
 *       }, {}),
 *     };
 *
 *     await RankingAlgorithmSettingsAPI.saveRankingWeights(selectedFlightModel, bodyData)
 *       .then((res) => {
 *         if (res.data.status === true) {
 *           messageToast("success", "Weights updated and normalised.");
 *           setParameterConfig((prev) =>
 *             prev.map((param) => ({
 *               ...param,
 *               valueFrom: "",
 *             }))
 *           );
 *           getRankingWeights();
 *         } else {
 *           messageToast("error", res.data?.message);
 *         }
 *       })
 *       .catch(() => {
 *         messageToast("error", "Failed to save configuration.");
 *       })
 *       .finally(() => {
 *         setSpinning(false);
 *       });
 *   }
 *
 *   async function resetToDefault() {
 *     setSpinning(true);
 *
 *     await RankingAlgorithmSettingsAPI.restoreDefaultWeights(selectedFlightModel)
 *       .then((res) => {
 *         if (res.data.status === true) {
 *           setParameterConfig((prev) =>
 *             prev.map((param) => ({
 *               ...param,
 *               valueFrom: "",
 *               valueTo: res.data.restored_weights[param.key],
 *             }))
 *           );
 *           messageToast("success", "Configuration reset to default successfully.");
 *         } else {
 *           messageToast("error", res.data?.message);
 *         }
 *       })
 *       .catch(() => {
 *         messageToast("error", "Failed to reset configuration to default.");
 *       })
 *       .finally(() => {
 *         setSpinning(false);
 *       });
 *   }
 *
 *   function handleInputChange(key, value) {
 *     setParameterConfig((prev) =>
 *       prev.map((param) =>
 *         param.key === key ? { ...param, valueFrom: value } : param
 *       )
 *     );
 *   }
 *
 *   return (
 *     <div className="container_div">
 *       <Helmet>
 *         <title>SpecOptimizer | Ranking Algorithm Settings</title>
 *       </Helmet>
 *       <AdminHeaderTab currrentTab="Ranking Algorithm Settings" />
 *       <Spin spinning={spinning}>
 *         <div className="admin_tab_content_wraper">
 *           <div className="table_head_wraper">
 *             <Text className="table_head">Ranking Algorithm Settings</Text>
 *           </div>
 *           ...
 *         </div>
 *       </Spin>
 *     </div>
 *   );
 * }
 * =========================================================================================
 */